import {useState, useEffect, useCallback} from "react";
import {
  chatConnectedResponse,
  ChatMessage,
  ChatRole,
  successLLMResponse,
  successSTTResponse,
} from "../logic/types/chat.types";
import {useSocket} from "src/hooks/socket/useSocket";
import {ConnectionState} from "src/hooks/socket/socket.type";
import {useAudioContext} from "src/hooks/audioContext/useAudioContext";
import {useVoicePlayer} from "src/hooks/voicePlayer/useVoicePlayer";
import {RECORDING_MAX_TIME} from "../assets/constants/chatOptions";
import {Artist, convertArtistToType} from "src/utils/artist.utils";

const SOCKET_EMIT_EVENTS = {
  AUDIO_STREAMING: "audio",
  STREAMING_END: "end",
  SEND_TEXT: "text",
  HEARTBEAT: "heartbeat",
};

const SOCKET_ON_EVENTS = {
  CHAT_CONNECTED: "chat:connected",
  CHAT_ERROR: "chat:error",
  STT_SUCCESS: "stt:done",
  STT_ERROR: "chat:stt-error",
  LLM_SUCCESS: "llm:done",
  LLM_ERROR: "chat:llm-error",
};

type UseChatParams = {
  artist: Artist;
  character: string;
  language: string;
  onExpire?: () => void;
};

export const useChat = ({
  artist,
  character,
  language,
  onExpire,
}: UseChatParams) => {
  const {connectionState, connect, disconnect, on, off, emit} = useSocket();
  const {audioState, startRecording, stopRecording, convertToUint8Array} =
    useAudioContext();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [socketEventError, setSocketEventError] = useState<boolean>(false);
  const [isProcessingAnswer, setIsProcessingAnswer] = useState<boolean>(false);
  const [recordingTimeRemaining, setRecordingTimeRemaining] =
    useState<number>(0);
  const [inputValue, setInputValue] = useState<string>("");
  const [endTimestamp, setEndTimestamp] = useState<number | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);
  const {playVoiceForText, stopVoice, activateVoice, isVoiceActivated} =
    useVoicePlayer(artist, character);

  const artistType = convertArtistToType(artist);

  const chatSocketConnect = async (): Promise<void> => {
    try {
      await connect(`${process.env.REACT_APP_SOCKET_SERVER_URL}/chat`, {
        transports: ["websocket"],
        withCredentials: true,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const subscribeChatSocketEvent = (): void => {
    on(SOCKET_ON_EVENTS.CHAT_CONNECTED, (data: chatConnectedResponse) => {
      console.log(`${SOCKET_ON_EVENTS.CHAT_CONNECTED} :: received`);
      console.log(data);
      const remaining = data.coupon?.remainingSeconds ?? 0;
      setEndTimestamp(Date.now() + remaining * 1000);
      setRemainingSeconds(remaining);
    });

    on(SOCKET_ON_EVENTS.CHAT_ERROR, (data: unknown) => {
      console.log(`${SOCKET_ON_EVENTS.CHAT_ERROR} :: received`);
      console.log(data);
      setSocketEventError(true);
    });

    on(SOCKET_ON_EVENTS.STT_SUCCESS, (data: successSTTResponse) => {
      console.log(`${SOCKET_ON_EVENTS.STT_SUCCESS} :: received`);
      const userMessage: ChatMessage = {
        content: data.text,
        role: ChatRole.USER,
        ts: Date.now(),
      };
      setMessages([userMessage]);
      setIsProcessingAnswer(true);
    });

    on(SOCKET_ON_EVENTS.STT_ERROR, (data: unknown) => {
      console.log(`${SOCKET_ON_EVENTS.STT_ERROR} :: received`);
      console.log(data);
      setIsProcessingAnswer(false);
      setSocketEventError(true);
    });

    on(SOCKET_ON_EVENTS.LLM_SUCCESS, (data: successLLMResponse) => {
      const assistantMessage: ChatMessage = {
        content: data.response,
        role: ChatRole.ASSISTANT,
        ts: Date.now(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setIsProcessingAnswer(false);
      playVoiceForText(data.response, data.intent);
    });

    on(SOCKET_ON_EVENTS.LLM_ERROR, (data: unknown) => {
      console.log(`${SOCKET_ON_EVENTS.LLM_ERROR} :: received`);
      console.log(data);
      setIsProcessingAnswer(false);
      setSocketEventError(true);
    });
  };

  const unsubscribeChatSocketEvent = (): void => {
    off(SOCKET_ON_EVENTS.CHAT_CONNECTED);
    off(SOCKET_ON_EVENTS.CHAT_ERROR);
    off(SOCKET_ON_EVENTS.STT_SUCCESS);
    off(SOCKET_ON_EVENTS.STT_ERROR);
    off(SOCKET_ON_EVENTS.LLM_SUCCESS);
    off(SOCKET_ON_EVENTS.LLM_ERROR);
  };

  const handleSendChatMessage = (message: string): void => {
    if (connectionState !== ConnectionState.CONNECTED) {
      return;
    }

    if (message.trim() === "") {
      return;
    }

    stopVoice();

    emit(SOCKET_EMIT_EVENTS.SEND_TEXT, {
      text: message.trim(),
      type: artistType,
      slave: character,
      language,
    });
    setIsProcessingAnswer(true);

    const userMessage: ChatMessage = {
      content: message.trim(),
      role: ChatRole.USER,
      ts: Date.now(),
    };

    setMessages([userMessage]);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(e.target.value);
  };

  const handleSubmitInput = (): void => {
    handleSendChatMessage(inputValue.trim());
    setInputValue("");
  };

  const clearSocketEventError = (): void => {
    setSocketEventError(false);
  };

  const startAudioRecording = async (): Promise<void> => {
    stopVoice();
    try {
      await startRecording();
      setRecordingTimeRemaining(RECORDING_MAX_TIME);
    } catch (error) {
      throw error;
    }
  };

  const stopAudioRecording = (): void => {
    stopVoice();

    const audioData = stopRecording();
    if (!audioData) {
      return;
    }

    const uint8Array = convertToUint8Array(audioData);
    if (uint8Array) {
      emit(SOCKET_EMIT_EVENTS.AUDIO_STREAMING, {
        audio: uint8Array,
        type: artistType,
        slave: character,
        language,
      });
      emit(SOCKET_EMIT_EVENTS.STREAMING_END, "");
      setIsProcessingAnswer(true);
    }
  };

  const handleStartAudioRecording = useCallback(async () => {
    await startAudioRecording();

    if (!isVoiceActivated) {
      try {
        await activateVoice();
      } catch (error) {
        console.error("Failed to activate audio:", error);
      }
    }
  }, [startAudioRecording, isVoiceActivated, activateVoice]);

  const handleStopAudioRecording = useCallback(() => {
    stopAudioRecording();
    setRecordingTimeRemaining(0);
  }, [stopAudioRecording]);

  // 마운트 시 자동 소켓 연결
  useEffect(() => {
    chatSocketConnect();

    return () => {
      disconnect();
    };
  }, []);

  // 연결 상태에 따른 자동 이벤트 구독
  useEffect(() => {
    if (connectionState === ConnectionState.CONNECTED) {
      console.log("소켓 연결 성공 :: 이벤트를 구독합니다.");
      subscribeChatSocketEvent();
    } else {
      console.log("소켓 연결 실패 :: 이벤트 구독을 해제합니다.");
      unsubscribeChatSocketEvent();
    }

    return () => {
      unsubscribeChatSocketEvent();
    };
  }, [connectionState]);

  useEffect(() => {
    return () => {
      stopVoice();
    };
  }, []);

  useEffect(() => {
    if (connectionState !== ConnectionState.CONNECTED) {
      return;
    }

    const interval = setInterval(() => {
      emit(SOCKET_EMIT_EVENTS.HEARTBEAT, "");
    }, 10000);

    return () => clearInterval(interval);
  }, [connectionState]);

  useEffect(() => {
    if (endTimestamp === null) {
      return;
    }

    if (connectionState !== ConnectionState.CONNECTED) {
      return;
    }

    const interval = setInterval(() => {
      const remaining = Math.max(
        0,
        Math.floor((endTimestamp - Date.now()) / 1000),
      );
      setRemainingSeconds(remaining);

      if (remaining === 0 && onExpire) {
        onExpire();
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [endTimestamp, connectionState, onExpire]);

  useEffect(() => {
    if (audioState !== "recording") {
      return;
    }

    const timer = setInterval(() => {
      setRecordingTimeRemaining((prev) => {
        if (prev <= 1) {
          handleStopAudioRecording();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [audioState]);

  return {
    connectionState,
    chatSocketConnect,
    chatSocketDisconnect: disconnect,
    messages,
    subscribeChatSocketEvent,
    unsubscribeChatSocketEvent,
    handleSendChatMessage,
    audioState,
    handleStartAudioRecording,
    handleStopAudioRecording,
    isProcessingAnswer,
    socketEventError,
    clearSocketEventError,
    isVoiceActivated,
    activateVoice,
    recordingTimeRemaining,
    inputValue,
    handleInputChange,
    handleSubmitInput,
    remainingSeconds,
  };
};
